import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { useForm } from 'react-hook-form'
import { Calendar, Euro, User, Mail, Phone, Building, ArrowLeft, CheckCircle } from 'lucide-react'
import { apiService as api } from '../services/api'
import toast from 'react-hot-toast'

const Reservation = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)

  const produitId = searchParams.get('produit_id')
  const startDate = searchParams.get('start_date')
  const endDate = searchParams.get('end_date')

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      notes: ''
    }
  })

  // Charger les paramètres admin
  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => api.get('/rentman/settings/public', { params: { t: Date.now() } }).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000
    }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const showPrice = backendShowPrice === true

  // Fetch product details
  const { data: productData, isLoading: productLoading } = useQuery(
    ['product', produitId, { start_date: startDate, end_date: endDate }],
    () => api.getProduct(produitId, { start_date: startDate, end_date: endDate }),
    {
      enabled: !!produitId
    }
  )

  // Create reservation mutation
  const createReservationMutation = useMutation(api.createReservation, {
    onSuccess: async (data) => {
      try {
        // Send confirmation email
        await api.sendConfirmationEmail(data.reservation.id)
        await api.sendNotificationEmail(data.reservation.id)
        
        navigate('/confirmation', { 
          state: { 
            reservation: data.reservation,
            client: data.reservation.client,
            produit: data.reservation.produit
          }
        })
      } catch (error) {
        console.error('Email sending error:', error)
        // Still navigate to confirmation even if email fails
        navigate('/confirmation', { 
          state: { 
            reservation: data.reservation,
            client: data.reservation.client,
            produit: data.reservation.produit
          }
        })
      }
    },
    onError: (error) => {
      toast.error('Erreur lors de la création de la réservation')
      console.error('Reservation creation error:', error)
    }
  })

  const produit = productData?.produit

  const calculateTotalPrice = () => {
    if (!startDate || !endDate || !produit) return 0
    
    const start = new Date(startDate)
    const end = new Date(endDate)
    // Correction: +1 pour compter le jour de fin inclus
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
    
    return days * parseFloat(produit.price_per_day)
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  const onSubmit = async (data) => {
    const token = localStorage.getItem('user_token')
    if (!token) {
      const redirect = `${window.location.pathname}${window.location.search}`
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`)
      return
    }

    if (step === 1) {
      setStep(2)
    } else if (step === 2) {
      // Create reservation
      createReservationMutation.mutate({
        produit_id: produitId,
        start_date: startDate,
        end_date: endDate,
        notes: data.notes
      })
    }
  }

  if (productLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    )
  }

  if (!produit) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Produit non trouvé</h2>
          <p className="text-gray-400 mb-6">Le produit que vous recherchez n'existe pas.</p>
          <button
            onClick={() => navigate('/catalogue-materiels')}
            className="btn-primary"
          >
            Retour au catalogue
          </button>
        </div>
      </div>
    )
  }

  const totalPrice = calculateTotalPrice()
  // Correction: +1 pour compter le jour de fin inclus
  const days = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1

  return (
    <div className="min-h-screen bg-black">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary-500 hover:text-primary-400 mb-4 group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Retour
          </button>
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-gradient">Réservation</span>
          </h1>
          <p className="text-xl text-gray-400">Finalisez votre réservation en quelques étapes</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Reservation Summary */}
          <div className="lg:col-span-1">
            <div className="card sticky top-8">
              <h3 className="text-xl font-semibold mb-6">Résumé de la réservation</h3>
              
              {/* Product Info */}
              <div className="mb-6">
                <div className="w-full h-32 bg-dark-700 rounded-lg mb-4 overflow-hidden">
                  {produit.image_url ? (
                    <img
                      src={produit.image_url}
                      alt={produit.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <Camera className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <h4 className="font-semibold mb-2">{produit.name}</h4>
                <p className="text-sm text-gray-400">{produit.category_name}</p>
              </div>

              {/* Dates */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Date de début</span>
                  <span className="font-medium">{new Date(startDate).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Date de fin</span>
                  <span className="font-medium">{new Date(endDate).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Durée</span>
                  <span className="font-medium">{days} jour{days > 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Price */}
              {showPrice && (
                <div className="border-t border-dark-700 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Prix par jour</span>
                    <span className="font-medium">{formatPrice(produit.price_per_day)}</span>
                  </div>
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-primary-500">{formatPrice(totalPrice)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reservation Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Step 1: Client Information */}
              {step === 1 && (
                <div className="card">
                  <div className="flex items-center mb-6">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-black font-bold mr-3">
                      1
                    </div>
                    <h2 className="text-2xl font-semibold">Informations client</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Prénom *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          {...register('first_name', { required: 'Prénom requis' })}
                          className="input-field pl-10"
                          placeholder="Votre prénom"
                        />
                      </div>
                      {errors.first_name && (
                        <p className="text-red-400 text-sm mt-1">{errors.first_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nom *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          {...register('last_name', { required: 'Nom requis' })}
                          className="input-field pl-10"
                          placeholder="Votre nom"
                        />
                      </div>
                      {errors.last_name && (
                        <p className="text-red-400 text-sm mt-1">{errors.last_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="email"
                          {...register('email', { 
                            required: 'Email requis',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Email invalide'
                            }
                          })}
                          className="input-field pl-10"
                          placeholder="votre@email.com"
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="tel"
                          {...register('phone', { required: 'Le téléphone est obligatoire' })}
                          className="input-field pl-10"
                          placeholder="06 12 34 56 78"
                        />
                        {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone.message}</p>}
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Entreprise
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          {...register('company')}
                          className="input-field pl-10"
                          placeholder="Nom de votre entreprise (optionnel)"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-8">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={createClientMutation.isLoading}
                    >
                      {createClientMutation.isLoading ? 'Création...' : 'Continuer'}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Confirmation */}
              {step === 2 && (
                <div className="card">
                  <div className="flex items-center mb-6">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-black font-bold mr-3">
                      2
                    </div>
                    <h2 className="text-2xl font-semibold">Confirmation</h2>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Notes supplémentaires (optionnel)
                    </label>
                    <textarea
                      {...register('notes')}
                      rows={4}
                      className="input-field"
                      placeholder="Ajoutez des informations utiles pour votre réservation..."
                    />
                  </div>

                  <div className="bg-dark-700 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold mb-2">Récapitulatif</h3>
                    <p className="text-sm text-gray-400">
                      En confirmant cette réservation, vous acceptez nos conditions générales de vente.
                      Une confirmation vous sera envoyée par email.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="btn-outline"
                    >
                      Retour
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={createReservationMutation.isLoading}
                    >
                      {createReservationMutation.isLoading ? 'Création...' : 'Confirmer la réservation'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Reservation
